import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  FileText, Edit2, X, Building2, ArrowLeft, ListChecks,
  Type, AlignLeft, List, CheckSquare, Calendar, Hash, Upload, Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkboxes', icon: CheckSquare },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'file', label: 'File Upload', icon: Upload },
  { value: 'note', label: 'Notes / Instructions', icon: Info }
];

const AdminCompanyApprovalQuestions = () => {
  const [steps, setSteps] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedStepFilter, setSelectedStepFilter] = useState('all');
  
  const [newQuestion, setNewQuestion] = useState({
    step_id: '',
    question_text: '',
    field_type: 'text',
    options: [],
    is_required: true,
    display_order: 0,
    placeholder: '',
    help_text: ''
  });
  const [optionsInput, setOptionsInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stepsRes, questionsRes] = await Promise.all([
        api.get('/admin/company-approval/steps'),
        api.get('/admin/company-approval/questions')
      ]);
      setSteps(stepsRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.step_id) {
      toast.error('Please select a step');
      return;
    }
    if (!newQuestion.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...newQuestion,
        options: optionsInput.split('\n').filter(o => o.trim())
      };
      
      await api.post('/admin/company-approval/questions', payload);
      toast.success('Question added successfully');
      setShowAddModal(false);
      setNewQuestion({
        step_id: '',
        question_text: '',
        field_type: 'text',
        options: [],
        is_required: true,
        display_order: 0,
        placeholder: '',
        help_text: ''
      });
      setOptionsInput('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuestion = async (questionId, updates) => {
    setSaving(true);
    try {
      await api.put(`/admin/company-approval/questions/${questionId}`, updates);
      toast.success('Question updated');
      setEditingQuestion(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await api.delete(`/admin/company-approval/questions/${questionId}`);
      toast.success('Question deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    }
  };

  const getStepName = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    return step?.name || 'Unknown Step';
  };

  const getStepOrder = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    return step?.order_number || 0;
  };

  // Group questions by step
  const questionsByStep = steps.reduce((acc, step) => {
    acc[step.id] = questions.filter(q => q.step_id === step.id).sort((a, b) => a.display_order - b.display_order);
    return acc;
  }, {});

  const filteredSteps = selectedStepFilter === 'all' 
    ? steps 
    : steps.filter(s => s.id === selectedStepFilter);

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin/company-approval-steps" className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </Link>
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListChecks className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Company Approval Questions</h1>
            </div>
            <p className="text-gray-500 text-sm ml-16">Define questions for each approval step that companies must answer.</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition font-medium text-sm"
            style={{ backgroundColor: BRAND_BLUE }}
            data-testid="add-question-btn"
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <select
            value={selectedStepFilter}
            onChange={(e) => setSelectedStepFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">All Steps</option>
            {steps.map(step => (
              <option key={step.id} value={step.id}>Step {step.order_number}: {step.name}</option>
            ))}
          </select>
        </div>

        {/* Questions grouped by Step */}
        {steps.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Steps Created Yet</h3>
            <p className="text-gray-500 mb-6">Create approval steps before adding questions.</p>
            <Link
              to="/admin/company-approval-steps"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition font-medium text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Plus className="w-4 h-4" />
              Create Steps
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredSteps.map(step => (
              <div key={step.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Step Header */}
                <div className="p-4 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                        {step.order_number}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{step.name}</h3>
                        {step.description && (
                          <p className="text-sm text-gray-500">{step.description}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-400">{questionsByStep[step.id]?.length || 0} questions</span>
                  </div>
                </div>

                {/* Questions */}
                <div className="divide-y divide-gray-100">
                  {(!questionsByStep[step.id] || questionsByStep[step.id].length === 0) ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      No questions in this step yet.
                    </div>
                  ) : (
                    questionsByStep[step.id].map((question, qIndex) => {
                      const FieldIcon = FIELD_TYPES.find(f => f.value === question.field_type)?.icon || Type;
                      
                      return (
                        <div key={question.id} className="p-4 flex items-start gap-4" data-testid={`question-${question.id}`}>
                          {/* Order & Icon */}
                          <div className="flex items-center gap-2 pt-1">
                            <GripVertical className="w-4 h-4 text-gray-300" />
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                              <FieldIcon className="w-4 h-4 text-gray-500" />
                            </div>
                          </div>

                          {/* Question Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2">
                              <p className="text-gray-900" dangerouslySetInnerHTML={{ __html: question.question_text }} />
                              {question.is_required && (
                                <span className="text-red-500 text-sm">*</span>
                              )}
                              {question.is_core && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">Core</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                              <span className="px-2 py-0.5 bg-gray-100 rounded-full">{question.field_type}</span>
                              {question.options && question.options.length > 0 && (
                                <span>{question.options.length} options</span>
                              )}
                              {question.help_text && (
                                <span className="text-blue-500">Has help text</span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            {!question.is_core && (
                              <>
                                <button
                                  onClick={() => setEditingQuestion(question)}
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(question.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Question Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Add Question</h2>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Step Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step *</label>
                <select
                  value={newQuestion.step_id}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, step_id: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  data-testid="new-question-step"
                >
                  <option value="">Select a step...</option>
                  {steps.map(step => (
                    <option key={step.id} value={step.id}>Step {step.order_number}: {step.name}</option>
                  ))}
                </select>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Text *</label>
                <textarea
                  value={newQuestion.question_text}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, question_text: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter your question (HTML supported)"
                  data-testid="new-question-text"
                />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <select
                  value={newQuestion.field_type}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, field_type: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                >
                  {FIELD_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Options (for select/checkbox) */}
              {(newQuestion.field_type === 'select' || newQuestion.field_type === 'checkbox') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options (one per line)</label>
                  <textarea
                    value={optionsInput}
                    onChange={(e) => setOptionsInput(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    placeholder="Option 1&#10;Option 2&#10;Option 3"
                  />
                </div>
              )}

              {/* Placeholder */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={newQuestion.placeholder}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, placeholder: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="Placeholder text..."
                />
              </div>

              {/* Help Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
                <textarea
                  value={newQuestion.help_text}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, help_text: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Additional help or instructions..."
                />
              </div>

              {/* Required */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newQuestion.is_required}
                  onChange={(e) => setNewQuestion(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                Required field
              </label>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQuestion}
                disabled={saving}
                className="px-5 py-2.5 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                style={{ backgroundColor: BRAND_BLUE }}
                data-testid="save-question-btn"
              >
                {saving ? 'Saving...' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Edit Question</h2>
                <button 
                  onClick={() => setEditingQuestion(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <EditQuestionForm 
              question={editingQuestion}
              steps={steps}
              onSave={(updates) => handleUpdateQuestion(editingQuestion.id, updates)}
              onCancel={() => setEditingQuestion(null)}
              saving={saving}
            />
          </div>
        </div>
      )}
    </Sidebar>
  );
};

// Separate component for edit form to manage its own state
const EditQuestionForm = ({ question, steps, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    question_text: question.question_text,
    field_type: question.field_type,
    is_required: question.is_required,
    placeholder: question.placeholder || '',
    help_text: question.help_text || ''
  });
  const [optionsInput, setOptionsInput] = useState((question.options || []).join('\n'));

  const handleSubmit = () => {
    const updates = {
      ...formData,
      options: optionsInput.split('\n').filter(o => o.trim())
    };
    onSave(updates);
  };

  return (
    <>
      <div className="p-6 space-y-4">
        {/* Question Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Question Text *</label>
          <textarea
            value={formData.question_text}
            onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
            rows={3}
          />
        </div>

        {/* Field Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
          <select
            value={formData.field_type}
            onChange={(e) => setFormData(prev => ({ ...prev, field_type: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          >
            {FIELD_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Options */}
        {(formData.field_type === 'select' || formData.field_type === 'checkbox') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Options (one per line)</label>
            <textarea
              value={optionsInput}
              onChange={(e) => setOptionsInput(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>
        )}

        {/* Placeholder */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
          <input
            type="text"
            value={formData.placeholder}
            onChange={(e) => setFormData(prev => ({ ...prev, placeholder: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Help Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
          <textarea
            value={formData.help_text}
            onChange={(e) => setFormData(prev => ({ ...prev, help_text: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
            rows={2}
          />
        </div>

        {/* Required */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={formData.is_required}
            onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
            className="rounded border-gray-300"
          />
          Required field
        </label>
      </div>
      <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 font-medium text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-5 py-2.5 text-white rounded-xl font-medium text-sm disabled:opacity-50"
          style={{ backgroundColor: BRAND_BLUE }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </>
  );
};

export default AdminCompanyApprovalQuestions;
